import {
  IMAGE_COMPRESS_SKIP_MAX_BYTES,
  IMAGE_TARGET_LONGEST_EDGE,
  IMAGE_TARGET_QUALITY,
} from "@/constants/uploadLimits";
import * as ImagePicker from "expo-image-picker";

type AssetWithFile = ImagePicker.ImagePickerAsset & { file?: File };

const loadSourceBlob = async (asset: AssetWithFile): Promise<Blob> => {
  if (asset.file) return asset.file;
  const response = await fetch(asset.uri);
  return response.blob();
};

const drawToBlob = async (
  bitmap: ImageBitmap,
  width: number,
  height: number,
): Promise<Blob> => {
  if (typeof OffscreenCanvas !== "undefined") {
    try {
      const offscreen = new OffscreenCanvas(width, height);
      const ctx = offscreen.getContext("2d");
      if (!ctx) throw new Error("no 2d context");
      ctx.drawImage(bitmap, 0, 0, width, height);
      return await offscreen.convertToBlob({
        type: "image/jpeg",
        quality: IMAGE_TARGET_QUALITY,
      });
    } catch {
      // Fall through to HTMLCanvasElement path.
    }
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");
  ctx.drawImage(bitmap, 0, 0, width, height);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      "image/jpeg",
      IMAGE_TARGET_QUALITY,
    );
  });
};

export const compressImage = async (
  asset: ImagePicker.ImagePickerAsset,
): Promise<ImagePicker.ImagePickerAsset> => {
  const assetWithFile = asset as AssetWithFile;
  const sourceBlob = await loadSourceBlob(assetWithFile);

  const bitmap = await createImageBitmap(sourceBlob);
  try {
    const srcWidth = bitmap.width;
    const srcHeight = bitmap.height;
    const longestSide = Math.max(srcWidth, srcHeight);
    const needsResize =
      longestSide > IMAGE_TARGET_LONGEST_EDGE && longestSide > 0;
    const smallEnough = sourceBlob.size <= IMAGE_COMPRESS_SKIP_MAX_BYTES;

    if (!needsResize && smallEnough) {
      return asset;
    }

    const scale = needsResize ? IMAGE_TARGET_LONGEST_EDGE / longestSide : 1;
    const targetWidth = Math.round(srcWidth * scale);
    const targetHeight = Math.round(srcHeight * scale);

    const outBlob = await drawToBlob(bitmap, targetWidth, targetHeight);

    const baseName =
      assetWithFile.file?.name?.replace(/\.[^.]+$/, "") ??
      asset.fileName?.replace(/\.[^.]+$/, "") ??
      "photo";
    const compressedFile = new File([outBlob], `${baseName}.jpg`, {
      type: "image/jpeg",
    });

    return {
      ...asset,
      uri: URL.createObjectURL(outBlob),
      width: targetWidth,
      height: targetHeight,
      fileSize: outBlob.size,
      mimeType: "image/jpeg",
      file: compressedFile,
    } as ImagePicker.ImagePickerAsset;
  } finally {
    bitmap.close();
  }
};
